import { useEffect, useRef, useState } from 'react';
import { FileWarning, LoaderCircle } from 'lucide-react';
import * as pdfjsLib from 'pdfjs-dist';

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ''}/pdf.worker.js`;

const PdfFirstPagePreview = ({ url, title }) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin: '160px' }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!isVisible || !url) return undefined;

    let cancelled = false;
    let loadingTask;
    let renderTask;

    const renderFirstPage = async () => {
      setStatus('loading');

      try {
        // Hostinger's CDN can reset PDF byte-range/stream requests.
        // Fetch the complete file for the small card thumbnail instead.
        loadingTask = pdfjsLib.getDocument({
          url,
          disableRange: true,
          disableStream: true,
        });
        const pdf = await loadingTask.promise;
        const page = await pdf.getPage(1);
        if (cancelled) return;

        const container = containerRef.current;
        const canvas = canvasRef.current;
        if (!container || !canvas) return;

        const baseViewport = page.getViewport({ scale: 1 });
        const widthScale = container.clientWidth / baseViewport.width;
        const heightScale = container.clientHeight / baseViewport.height;
        const cssScale = Math.min(widthScale, heightScale);
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
        const renderViewport = page.getViewport({ scale: cssScale * pixelRatio });

        canvas.width = Math.floor(renderViewport.width);
        canvas.height = Math.floor(renderViewport.height);
        canvas.style.width = `${Math.floor(renderViewport.width / pixelRatio)}px`;
        canvas.style.height = `${Math.floor(renderViewport.height / pixelRatio)}px`;

        renderTask = page.render({
          canvasContext: canvas.getContext('2d'),
          viewport: renderViewport,
        });
        await renderTask.promise;

        if (!cancelled) setStatus('ready');
      } catch (error) {
        if (!cancelled && error?.name !== 'RenderingCancelledException') {
          setStatus('error');
        }
      }
    };

    renderFirstPage();

    return () => {
      cancelled = true;
      renderTask?.cancel();
      loadingTask?.destroy();
    };
  }, [isVisible, url]);

  return (
    <div
      ref={containerRef}
      className="relative flex aspect-[210/297] w-full items-center justify-center overflow-hidden bg-slate-100"
    >
      <canvas
        ref={canvasRef}
        className={`max-h-full max-w-full shadow-sm transition-opacity duration-300 ${status === 'ready' ? 'opacity-100' : 'opacity-0'}`}
        aria-label={`${title} first page preview`}
      />

      {status === 'loading' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-100 to-slate-200 text-slate-500">
          <LoaderCircle size={26} className="animate-spin text-accentBlue" />
          <span className="text-xs font-semibold">Loading first page…</span>
        </div>
      )}

      {status === 'error' && (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-100 to-slate-200 px-5 text-center text-slate-500">
          <FileWarning size={30} className="text-slate-400" />
          <span className="text-xs font-semibold">Preview unavailable</span>
        </div>
      )}
    </div>
  );
};

export default PdfFirstPagePreview;
